"use client";

/**
 * Login page — both auth methods visible from the start.
 *
 * The form shows email + password directly, plus a "Sign in with Microsoft"
 * button below a divider. Either button is reachable in one click — no
 * progressive disclosure, no hidden state.
 *
 * Flow per button:
 *   - "Sign in"               → standard password flow (useLogin hook)
 *   - "Sign in with Microsoft" → POST /auth/sso/discover with the email
 *                                and redirect to the returned authorize URL
 *
 * The Microsoft button requires the email so the backend can resolve the
 * customer's tenant and prefill ``domain_hint`` on the Microsoft sign-in
 * page. Without it the user would have to type their email twice.
 */

import { useCallback, useEffect, useState, type FormEvent } from "react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { useLogin } from "@/lib/hooks/use-login";
import { Spinner } from "@/components/ui/spinner";
import { MFAForm } from "./mfa-form";
import { SessionConflictDialog } from "./session-conflict-dialog";
import { discover } from "@/lib/api/sso";
import { getDeviceFingerprint, getDeviceName } from "@/lib/auth/device";
import { APIClientError } from "@/lib/api/client";
import { captureEvent, standardBody, useCaptureOnce } from "@/lib/analytics";

const ERROR_MESSAGES: Record<string, string> = {
  company_inactive:
    "Your company account is currently inactive. Please contact your company administrator for assistance.",
  // ``account_locked`` is augmented with a live countdown via
  // ``LockoutCountdown`` below — the static fallback only appears if the
  // backend didn't include ``locked_until`` (e.g. on an older deployment).
  account_locked:
    "Your account is temporarily locked due to too many failed attempts. Please wait a few minutes or contact your administrator.",
  account_disabled:
    "Your account has been disabled. Please contact your company administrator.",
  session_limit_reached:
    "You have reached the maximum number of concurrent sessions. Please sign out of another device first, or contact your administrator.",
  ip_not_allowed:
    "Access denied: your IP address is not on your company's allowed list. Contact your administrator if you believe this is an error.",
  password_login_disabled_for_sso_user:
    "This account uses Microsoft to sign in. Please use the Microsoft button below.",
  password_login_disabled_by_company_policy:
    "Your organisation requires Microsoft sign-in for all users. Contact your administrator to switch your account to Microsoft SSO.",
};

function formatLoginError(code: string, fallback: string, retryAfter?: number): string {
  if (code === "rate_limited") {
    return retryAfter && retryAfter > 0
      ? `Too many attempts. Please try again in ${retryAfter} seconds.`
      : "Too many attempts. Please wait a moment before trying again.";
  }
  return ERROR_MESSAGES[code] ?? fallback;
}

function LockoutCountdown({ lockedUntil }: { lockedUntil: string }) {
  // Re-render every second so the visible countdown stays accurate. We
  // pull "now" from a tick state rather than ``Date.now()`` outside the
  // effect to keep the component pure-ish for React's strict-mode passes.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const target = new Date(lockedUntil).getTime();
  const remainingMs = Math.max(0, target - now);
  if (remainingMs <= 0) {
    return (
      <span className="font-medium text-success">
        Lockout cleared - you can sign in now.
      </span>
    );
  }
  const totalSeconds = Math.ceil(remainingMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const formatted = `${minutes}:${seconds.toString().padStart(2, "0")}`;
  return (
    <span className="font-medium tabular-nums">Try again in {formatted}</span>
  );
}

const SUCCESS_MESSAGES: Record<string, string> = {
  password_changed: "Password changed successfully. Please sign in with your new password.",
};

const REASON_MESSAGES: Record<string, string> = {
  session_idle_timeout:
    "You were signed out because your session was inactive. Please sign in to continue.",
  invalid_refresh_token:
    "Your session was ended, likely because you signed in from another device. Please sign in again.",
  refresh_token_reuse_detected:
    "For your security, we ended all active sessions for this account. Please sign in again.",
  company_inactive:
    "Your company account is not currently active. Please contact your administrator.",
  user_inactive:
    "Your account is not currently active. Please contact your administrator.",
  network_error:
    "We could not reach the server. Please check your connection and sign in again.",
  session_ended:
    "Your session has ended. Please sign in to continue.",
};

function resolveReasonMessage(code: string | null): string | null {
  if (!code) return null;
  return REASON_MESSAGES[code] ?? REASON_MESSAGES["session_ended"] ?? null;
}

export default function LoginPage() {
  const { state, submitLogin, submitMFA, submitWebAuthnMFA, confirmSessionTakeover, reset } =
    useLogin();
  const searchParams = useSearchParams();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [ssoBusy, setSsoBusy] = useState(false);
  const [ssoError, setSsoError] = useState<string | null>(null);

  const successMessage = SUCCESS_MESSAGES[searchParams.get("message") ?? ""];
  const reasonMessage = resolveReasonMessage(searchParams.get("reason"));

  // Pageview event — fires once per /login mount. Anonymous distinct_id
  // is generated by posthog-js (memory persistence per master decision
  // #4). After a successful login the bridge calls posthog.identify(),
  // which aliases the anonymous id onto the user's uuid so this event
  // appears on the identified user's timeline (Plan_01 § "Pre-auth
  // tracking behavior").
  useCaptureOnce(() => ({
    event_name: "auth.login.viewed",
    screen: "auth/login",
    ...standardBody(),
  }));

  const handlePasswordSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setSsoError(null);
      captureEvent({
        event_name: "auth.login.submitted",
        screen: "auth/login",
        method: "password",
        ...standardBody(),
      });
      await submitLogin(email, password);
    },
    [email, password, submitLogin],
  );

  const handleSsoClick = useCallback(async () => {
    setSsoError(null);
    setSsoBusy(true);
    // Microsoft button click fires the same submitted event as the
    // password form, distinguished by ``method``. We fire BEFORE the
    // SSO discover round-trip so a rejected discover (e.g. unknown
    // email → password path) still leaves the analytics record of the
    // user's *intent* to use SSO.
    captureEvent({
      event_name: "auth.login.submitted",
      screen: "auth/login",
      method: "microsoft_sso",
      ...standardBody(),
    });
    try {
      const fingerprint = await getDeviceFingerprint();
      const deviceName = getDeviceName();
      // Email is optional. If the user typed one, we pass it so
      // Microsoft can pre-pick the tenant + account (smoother UX). If
      // not, we kick off SSO without hints — Microsoft asks the user
      // to pick an account, then sends them back to our callback which
      // resolves the company via the validated ``tid`` claim.
      const trimmed = email.trim();
      const result = await discover({
        ...(trimmed ? { email: trimmed } : {}),
        device_fingerprint: fingerprint,
        device_name: deviceName,
      });

      if (result.method === "sso_entra" && result.authorize_url) {
        // Hard navigation off our origin to Microsoft. The browser tab
        // becomes Microsoft's domain for the auth handshake; on success
        // we land on /auth/callback/microsoft with code+state.
        window.location.href = result.authorize_url;
        return;
      }

      // Backend resolved the typed email to the password path. We
      // deliberately do NOT distinguish reasons here — that would let
      // an attacker enumerate accounts via the Microsoft button.
      // Generic message; the user types a password and proceeds.
      setSsoBusy(false);
      setSsoError(
        "This email is not set up for Microsoft sign-in. Use email and password below, or contact your administrator if you believe this is wrong.",
      );
    } catch (err) {
      setSsoBusy(false);
      if (err instanceof APIClientError && err.status === 429) {
        setSsoError(
          "Too many sign-in attempts. Please wait a moment and try again.",
        );
      } else {
        setSsoError(
          "We couldn't reach the sign-in service. Please try again.",
        );
      }
    }
  }, [email]);

  // ── Render branches ─────────────────────────────────────

  if (state.step === "mfa_required") {
    return (
      <MFAForm
        userId={state.userId}
        mfaMethods={state.mfaMethods}
        onSubmit={submitMFA}
        onWebAuthnComplete={submitWebAuthnMFA}
        onBack={reset}
      />
    );
  }

  if (state.step === "redirecting") {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (state.step === "session_conflict") {
    return (
      <SessionConflictDialog
        activeDevice={state.activeDevice}
        lastActivity={state.lastActivity}
        onConfirm={confirmSessionTakeover}
        onCancel={reset}
      />
    );
  }

  const isLoading = state.step === "loading";
  const error =
    state.step === "error"
      ? formatLoginError(state.code, state.message, state.retryAfter)
      : ssoError;

  // When the password endpoint surfaces password_login_disabled_for_sso_user
  // we know the email belongs to an SSO account. Steer the user to the
  // Microsoft button rather than letting them re-type passwords. The
  // company-policy variant gets the same treatment — both mean "use SSO".
  const hintMicrosoftButton =
    state.step === "error" &&
    (state.code === "password_login_disabled_for_sso_user" ||
      state.code === "password_login_disabled_by_company_policy");

  return (
    <div className="rounded-xl border border-border bg-surface p-8 shadow-sm">
      <h2 className="mb-6 text-lg font-medium text-text-primary">Sign in</h2>

      {successMessage ? (
        <div className="mb-4 rounded-lg bg-success-light px-3 py-2.5 text-sm text-success">
          {successMessage}
        </div>
      ) : null}

      {reasonMessage ? (
        <div className="mb-4 rounded-lg border border-warning/40 bg-warning-light px-3 py-2.5 text-sm text-text-primary">
          {reasonMessage}
        </div>
      ) : null}

      <form onSubmit={handlePasswordSubmit} className="space-y-4">
        <div>
          <label
            htmlFor="email"
            className="mb-1.5 block text-sm font-medium text-text-primary"
          >
            Email
          </label>
          <input
            id="email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isLoading || ssoBusy}
            className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder-text-tertiary focus:border-border-focus focus:ring-2 focus:ring-ring focus:outline-none disabled:opacity-50 max-md:min-h-11 max-md:text-base"
            placeholder="you@company.com"
          />
        </div>

        <div>
          <label
            htmlFor="password"
            className="mb-1.5 block text-sm font-medium text-text-primary"
          >
            Password
          </label>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading || ssoBusy}
              className="w-full rounded-lg border border-border bg-surface px-3 py-2 pr-10 text-sm text-text-primary placeholder-text-tertiary focus:border-border-focus focus:ring-2 focus:ring-ring focus:outline-none disabled:opacity-50 max-md:min-h-11 max-md:text-base"
              placeholder="Enter your password"
            />
            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-text-tertiary hover:text-text-primary"
              tabIndex={-1}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="size-4">
                  <path fillRule="evenodd" d="M3.28 2.22a.75.75 0 0 0-1.06 1.06l14.5 14.5a.75.75 0 1 0 1.06-1.06l-1.745-1.745a10.029 10.029 0 0 0 3.3-4.38 1.651 1.651 0 0 0 0-1.185A10.004 10.004 0 0 0 9.999 3a9.956 9.956 0 0 0-4.744 1.194L3.28 2.22ZM7.752 6.69l1.092 1.092a2.5 2.5 0 0 1 3.374 3.373l1.092 1.092a4 4 0 0 0-5.558-5.558Z" clipRule="evenodd" />
                  <path d="m10.748 13.93 2.523 2.523A9.987 9.987 0 0 1 10 17c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 0 1 3.04-4.431l2.59 2.59a4 4 0 0 0 4.66 4.66Z" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="size-4">
                  <path d="M10 12.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z" />
                  <path fillRule="evenodd" d="M.664 10.59a1.651 1.651 0 0 1 0-1.186A10.004 10.004 0 0 1 10 3c4.257 0 7.893 2.66 9.336 6.41.147.381.146.804 0 1.186A10.004 10.004 0 0 1 10 17c-4.257 0-7.893-2.66-9.336-6.41ZM14 10a4 4 0 1 1-8 0 4 4 0 0 1 8 0Z" clipRule="evenodd" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {error ? (
          <div className="rounded-lg bg-error-light px-3 py-2.5 text-sm text-error">
            {error}
            {hintMicrosoftButton ? (
              <span className="ml-1 text-text-secondary">
                Use the Microsoft button below.
              </span>
            ) : null}
            {state.step === "error"
              && state.code === "account_locked"
              && state.lockedUntil ? (
              <div className="mt-1 text-xs text-text-secondary">
                <LockoutCountdown lockedUntil={state.lockedUntil} />
              </div>
            ) : null}
          </div>
        ) : null}

        <button
          type="submit"
          disabled={isLoading || ssoBusy}
          className="w-full rounded-lg bg-accent px-4 py-2.5 text-sm font-medium text-accent-text shadow-sm hover:bg-accent-hover focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-surface focus:outline-none disabled:opacity-50 max-md:min-h-11"
        >
          {isLoading ? "Signing in..." : "Sign in"}
        </button>

        {/* Visual divider — keeps the two methods side-by-side equal in
            visual weight without stacking them as a pile of buttons. */}
        <div className="flex items-center gap-3 py-1">
          <div className="h-px flex-1 bg-border" />
          <span className="text-xs uppercase tracking-wide text-text-tertiary">
            or
          </span>
          <div className="h-px flex-1 bg-border" />
        </div>

        <button
          type="button"
          onClick={handleSsoClick}
          disabled={isLoading || ssoBusy}
          className="flex w-full items-center justify-center gap-3 rounded-lg border border-border bg-surface px-4 py-2.5 text-sm font-medium text-text-primary shadow-sm hover:bg-surface-hover focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-surface focus:outline-none disabled:opacity-50 max-md:min-h-11"
        >
          <MicrosoftMark />
          {ssoBusy ? "Redirecting to Microsoft..." : "Sign in with Microsoft"}
        </button>
      </form>

      <div className="mt-4 text-center">
        <Link
          href="/password/reset"
          className="text-sm text-text-secondary hover:text-text-primary"
        >
          Forgot your password?
        </Link>
      </div>
    </div>
  );
}

function MicrosoftMark() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 21 21"
      className="h-4 w-4"
      aria-hidden="true"
    >
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}
